import { useIntl } from 'react-intl';

import { IHistoricalOrder } from '@rbi-ctg/menu';

import { IUserOrderFragment, ServiceMode } from '../../generated/rbi-graphql';

import { messages } from './messages';
import { IOrderRefundEligibilityData, IServiceModeRequirements } from './types';
import {
  checkIsDeliveryActive,
  checkIsOrderDelivery,
  checkIsOrderStatusRefundable,
  checkIsOrderStatusRefunded,
  checkServiceModeEnabled,
  checkServiceModeRequirements,
  getFirstMessage,
  useCheckRefundUnderMaxPrice,
  useLaunchDarklyCirServiceModes
} from './utils';

/**
 * Checks the provided order to determine whether or not
 * a customer initiated refund can be performed.
 */
export const useCheckOrderRefundEligibility = (
  order?: IHistoricalOrder | IUserOrderFragment | null
): IOrderRefundEligibilityData => {
  const { formatMessage } = useIntl();
  const {
    isDeliveryLDEnabled,
    isCurbsideLDEnabled,
    isDriveThruLDEnabled,
    isTakeoutLDEnabled
  } = useLaunchDarklyCirServiceModes();

  const isOrderUnderMaxRefundPrice = useCheckRefundUnderMaxPrice(order);

  const isOrderDelivery = checkIsOrderDelivery(order);
  const isDeliveryActive = checkIsDeliveryActive(order);
  const isOrderStatusRefundable = checkIsOrderStatusRefundable(order);
  const isOrderStatusRefunded = checkIsOrderStatusRefunded(order);

  /**
   * Lists enabled service modes and mode specific requirements
   */
  const serviceModeRequirements: IServiceModeRequirements = {
    allServiceModes: [isOrderUnderMaxRefundPrice, isOrderStatusRefundable],
    [ServiceMode.DELIVERY]: [isDeliveryLDEnabled, !isDeliveryActive],
    [ServiceMode.DRIVE_THRU]: [isDriveThruLDEnabled],
    [ServiceMode.CURBSIDE]: [isCurbsideLDEnabled],
    [ServiceMode.TAKEOUT]: [isTakeoutLDEnabled]
  };

  const isOrderEligibleForRefund =
    // Verify that the service mode is enabled
    checkServiceModeEnabled(serviceModeRequirements, order) &&
    // Check that the global order checks pass
    checkServiceModeRequirements(serviceModeRequirements.allServiceModes) &&
    // Check that the service mode specific requirements pass.
    checkServiceModeRequirements(serviceModeRequirements[order?.cart.serviceMode || '']);

  // Messaging to be displayed on the support form if the user is not allowed to obtain a refund.
  // The message to be displayed on the support form is listed in order of precedence should multiple reasons apply to the user.
  const refundIneligibilityReason = getFirstMessage([
    // Is delivery active
    isDeliveryActive && formatMessage(messages.supportFormDeliveryActive),
    // is order over refund price
    !isOrderUnderMaxRefundPrice && formatMessage(messages.supportFormRefundGenericError),
    // has the order already been refunded
    isOrderStatusRefunded && formatMessage(messages.supportFormOrderAlreadyRefunded),
    // fallback if order status is something else
    !isOrderStatusRefundable && formatMessage(messages.supportFormRefundGenericError)
  ]);

  return {
    isOrderEligibleForRefund,
    isOrderDelivery,
    isOrderUnderMaxRefundPrice,
    isDeliveryActive,
    isOrderStatusRefundable,
    refundIneligibilityReason
  };
};
